/**
 * Tema claro / oscuro.
 *
 * La preferencia vive en `data-theme` sobre `<html>`, que es lo que lee el CSS.
 *
 * Solo hay dos opciones. Hubo una tercera, "Sistema", y se quitó: a cambio de
 * un botón más ofrecía algo que ya ocurre solo, porque en la PRIMERA visita,
 * cuando no hay nada guardado, se arranca con la preferencia del dispositivo.
 * A partir de ahí manda lo que la persona haya elegido, que es lo que espera
 * de un interruptor.
 */

export type ThemeChoice = 'light' | 'dark';
/** Alias histórico: ya no hay diferencia entre elegido y resuelto. */
export type ResolvedTheme = ThemeChoice;

const STORAGE_KEY = 'open_dahu_theme';

export function systemTheme(): ThemeChoice {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/**
 * La preferencia guardada, o la del dispositivo si no hay ninguna.
 *
 * Un `'system'` guardado por la versión anterior cuenta como "sin elegir": se
 * resuelve aquí al tema del dispositivo y se queda fijado en cuanto se toque el
 * interruptor. Así nadie se queda con un valor que ya no existe.
 */
export function readChoice(): ThemeChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    /* almacenamiento bloqueado: se usa el del sistema */
  }
  return systemTheme();
}

export function applyTheme(choice: ThemeChoice): ThemeChoice {
  document.documentElement.dataset.theme = choice;

  // La barra del navegador en móvil también cambia: si no, queda un borde
  // oscuro sobre una página clara.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', choice === 'light' ? '#F5F8FC' : '#060A12');

  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* sin persistencia: el tema dura lo que la pestaña */
  }
  return choice;
}
